import { yawFromRosOrientation } from "lib/protocols/mapFilterYaml";

/** Max |Δx| / |Δy| (metres) before prompting for a waypoint before front TakePhoto. */
export const FRONT_PHOTO_WAYPOINT_XY_THRESHOLD_M = 0.3;

/** Max |Δyaw| (degrees) before prompting for a waypoint before front TakePhoto. */
export const FRONT_PHOTO_WAYPOINT_YAW_THRESHOLD_DEG = 7.5;

export type RobotPoseLike = {
    position?: { x?: number; y?: number; z?: number } | null;
    orientation?: { x?: number; y?: number; z?: number; w?: number } | null;
} | null | undefined;

export type MissionActionLike = {
    actionType: string;
    args?: unknown;
};

/** Canonical video_pipeline front camera id (`/camera/list`). */
export function isFrontCameraName(cameraName: string | null | undefined): boolean {
    return String(cameraName || "").trim().toLowerCase() === "front";
}

function wrapDegreesTo180(deg: number): number {
    let d = deg % 360;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    return d;
}

function posePositionXY(pose: RobotPoseLike): { x: number; y: number } | null {
    const x = pose?.position?.x;
    const y = pose?.position?.y;
    if (typeof x !== "number" || typeof y !== "number" || !Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
    }
    return { x, y };
}

function poseYawDeg(pose: RobotPoseLike): number | null {
    const ori = pose?.orientation;
    if (
        !ori ||
        typeof ori.x !== "number" ||
        typeof ori.y !== "number" ||
        typeof ori.z !== "number" ||
        typeof ori.w !== "number"
    ) {
        return null;
    }
    return (yawFromRosOrientation(ori as { x: number; y: number; z: number; w: number }) * 180) / Math.PI;
}

/**
 * Prior NavigateTo for a front-photo check: last NavigateTo before `insertionIndex`
 * when inserting mid-list; otherwise the last NavigateTo in the list.
 */
export function findPriorNavigateToPose(
    actions: MissionActionLike[],
    insertionIndex: number | null
): RobotPoseLike {
    const candidates =
        insertionIndex === null
            ? actions
            : actions.slice(0, Math.max(0, insertionIndex));

    for (let i = candidates.length - 1; i >= 0; i--) {
        const action = candidates[i];
        if (action.actionType !== "NavigateTo") continue;
        const args = (action.args ?? {}) as { pose?: RobotPoseLike };
        if (args.pose) return args.pose;
    }
    return null;
}

/**
 * Whether to ask the user to record a waypoint before a front-camera TakePhoto.
 * Returns false when current pose is missing (skip prompt; create photo only).
 * Returns true when there is no prior waypoint or pose delta exceeds thresholds.
 */
export function shouldPromptWaypointBeforeFrontPhoto(options: {
    currentPose: RobotPoseLike;
    priorWaypointPose: RobotPoseLike;
}): boolean {
    const { currentPose, priorWaypointPose } = options;
    const currentXY = posePositionXY(currentPose);
    if (!currentXY) return false;

    const priorXY = posePositionXY(priorWaypointPose);
    if (!priorXY) return true;

    if (
        Math.abs(currentXY.x - priorXY.x) > FRONT_PHOTO_WAYPOINT_XY_THRESHOLD_M ||
        Math.abs(currentXY.y - priorXY.y) > FRONT_PHOTO_WAYPOINT_XY_THRESHOLD_M
    ) {
        return true;
    }

    const currentYaw = poseYawDeg(currentPose);
    const priorYaw = poseYawDeg(priorWaypointPose);
    if (currentYaw === null || priorYaw === null) {
        // XY within threshold but yaw unknown — do not prompt on rotation alone.
        return false;
    }

    return Math.abs(wrapDegreesTo180(currentYaw - priorYaw)) > FRONT_PHOTO_WAYPOINT_YAW_THRESHOLD_DEG;
}
